import {DossierFlowType, WorkingActions} from '../../../../models/enums.model';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {AuConfirmWorkingActionDialogComponent} from '../../au-confirm-working-action-dialog/au-confirm-working-action-dialog.component';
import {AuthorityQuotationDeclineDialogComponent} from '../../authority-quotation-decline-dialog/authority-quotation-decline-dialog.component';
import {WorkingModel} from '../../../../models/working.model';
import {QuotationListInfoModel} from '../../../../models/quotation-list-info.model';
import {BehaviorSubject, concat, of, Subject} from 'rxjs';
import {AppConfigEnvironment} from '@shared/models/appconfig-environment.model';
import {QuotationService, QuotationServiceActionType} from '../../../../services/quotation.service';
import {MessageManagerService} from '@shared/service/message-manager/message-manager.service';
import {AuthorityWarrantyDialogComponent} from '../../../authority-warranty-dialog/authority-warranty-dialog.component';
import {MultiUpdateWarrantyRequestBody, WarrantyService} from '../../../../services/warranty.service';
import {Utilities} from '@shared/utilities/utilities.model';
import {QuotationEntry} from '../working-manager.service';
import {LoaderService} from '@shared/service/loader.service';
import {AlertService, AuthorityEntityType} from '../../../../services/alert.service';
import {MODULE_AUTHORITY_KEY} from '../../../../../../permissions-variables';
import {DossierModel} from '../../../../models/dossier.model';
import {AlertReaderWithCommodity, NCReadersModel} from '../../../../../../notification-center/models/nc-readers.model';
import {NotificationCenterUtilities} from '../../../../../../notification-center/utilities/notification-center.utilities';
import {concatMap, delay, map, skip, takeWhile, tap} from 'rxjs/operators';
import {DateUtilities} from '@shared/utilities/date-utilities.model';
import {DocumentsService} from '../../../../services/documents.service';
import {MessageManagerUtilities} from '@shared/service/message-manager/message-manager-utilities';

/**
 * Abstract class that contains the common action that all user can do
 */

export abstract class CommonActions {
	static mapText: Map<WorkingActions, string> = new Map<WorkingActions, string>([
		[WorkingActions.START_WORKING, 'Stai per iniziare la lavorazione. Sicuro?'],
		[WorkingActions.FINISH_WORKING, 'Stai per terminare la lavorazione. Sicuro?'],
		[WorkingActions.BILL_WITHOUT_LAST_INTEGRATION, 'Stai per terminare la lavorazione. Sicuro?']
	]);

	static openConfirmActionDialog(dossierflowType: string, dialog: MatDialog, action: WorkingActions, toDo: () => void, text?: string): void {
		const dialogRef: MatDialogRef<AuConfirmWorkingActionDialogComponent> = dialog.open(AuConfirmWorkingActionDialogComponent, {
			height: 'auto',
			width: 'auto',
			backdropClass: 'amd-dialog__backdrop--dark',
			disableClose: true,
			data: {
				text: (dossierflowType && dossierflowType === DossierFlowType.REPAIR && action === WorkingActions.FINISH_WORKING) ?
					`Ricordati di caricare la fattura. \nTerminare la lavorazione?` : (text || this.mapText.get(action))
			}
		}) as MatDialogRef<AuConfirmWorkingActionDialogComponent>;

		dialogRef.afterClosed().subscribe((confirmAction: boolean) => {
			if (confirmAction) {
				toDo();
			}
		});
	}

	static openDeclineDialog(
		dialog: MatDialog, dossierId: string, working: WorkingModel,
		allQuotations: QuotationListInfoModel, needRefreshDossierSubject: Subject<void>, context: string, declined_note_required?: boolean, dossier?: DossierModel
	): void {
		const dialogRefProc: MatDialogRef<AuthorityQuotationDeclineDialogComponent> = dialog.open(AuthorityQuotationDeclineDialogComponent, {
			height: 'auto',
			width: 'auto',
			backdropClass: 'amd-dialog__backdrop--dark',
			disableClose: true,
			data: {
				category: working.type,
				dossierId: dossierId,
				workingId: working.id,
				workingNumber: working.working_number_prefix,
				dossierWorkingQualification: working.dossierWorkingQualification,
				quotationId: allQuotations.quotations[0].id,
				context: context,
				quotation: allQuotations.quotations[0],
				declined_note_required: declined_note_required,
				dossier: dossier,
				working: working,
			}
		});

		dialogRefProc.afterClosed().subscribe(
			(result: boolean) => {
				if (result) {
					needRefreshDossierSubject.next();
				}
			}, (error) => {
				console.error('error in closing', error);
			}
		);
	}

	static editQuotation(
		quotationService: QuotationService, messageManagerService: MessageManagerService, onEditedQuotationSubject: Subject<any>, loaderService: LoaderService,
		environment: AppConfigEnvironment, dossier: DossierModel, working: WorkingModel, allQuotations: QuotationListInfoModel
	): void {
		const body: any = {callback_fe_url: environment.QUOTATION_WAITING_PAGE};
		const quotationID: string = allQuotations.quotations.find(item => item.type === working.type).id;
		loaderService.display(true);
		quotationService.editIframe(body, dossier, quotationID, working.type, working.id,
			response => {
				loaderService.display(false);
				onEditedQuotationSubject.next(response);
			}, message => {
				loaderService.display(false);
				messageManagerService.pushInnerMessage(message);
			}
		);
	}

	static deleteQuotation(
		quotationService: QuotationService, messageManagerService: MessageManagerService, needRefreshDossierSubject: Subject<void>,
		loaderService: LoaderService, dossier: DossierModel, workingId: string, allQuotations: QuotationListInfoModel
	): void {
		loaderService.display(true);
		quotationService.deleteQuotation(dossier, allQuotations.quotations[0].id, workingId,
			successMessage => {
				needRefreshDossierSubject.next();
				messageManagerService.pushInnerMessage(successMessage);
			}, errorMessage => {
				messageManagerService.pushInnerMessage(errorMessage);
			}
		);
	}

	static multiChangeWarranty(
		dialog: MatDialog, tableSelectedEntry: QuotationEntry[], loaderService: LoaderService, warrantyService: WarrantyService,
		needRefreshQuotationsSubject: Subject<void>, messageManagerService: MessageManagerService,
		dossierId: string, workingId: string, quotationId: string
	): void {
		const dialogRef: MatDialogRef<AuthorityWarrantyDialogComponent> = dialog.open(AuthorityWarrantyDialogComponent, {
			height: 'auto',
			width: '500px',
			backdropClass: 'amd-dialog__backdrop--dark',
			disableClose: true,
			data: {
				multiEditWarrantyType: true,
				selectedEntries: tableSelectedEntry
			}
		});

		dialogRef.afterClosed().subscribe((requestBody: MultiUpdateWarrantyRequestBody) => {
			if (!Utilities.isVoid(requestBody)) {
				CommonActions.updateWarranty(requestBody, loaderService, warrantyService, needRefreshQuotationsSubject,
					messageManagerService, dossierId, workingId, quotationId);
			}
		});
	}

	static updateWarranty(
		requestBody: MultiUpdateWarrantyRequestBody, loaderService: LoaderService, warrantyService: WarrantyService,
		needRefreshQuotationsSubject: Subject<void>, messageManagerService: MessageManagerService,
		dossierId: string, workingId: string, quotationId: string
	): void {
		loaderService.display(true);
		warrantyService.updateMultiWarranty(dossierId, workingId,
			quotationId, requestBody,
			message => {
				needRefreshQuotationsSubject.next();
				messageManagerService.pushInnerMessage(message);
			},
			message => {
				messageManagerService.pushInnerMessage(message);
			}
		);
	}

	static doQuotationPatch(quotationService: QuotationService, loaderService: LoaderService, dossierId: string, workingId: string, allQuotations: QuotationListInfoModel,
							needRefreshDossierSubject: Subject<void>, messageManagerService: MessageManagerService, quotationActionType: QuotationServiceActionType): void {
		loaderService.display(true);
		quotationService.quotationServiceDoPatch(dossierId, workingId,
			allQuotations.quotations[0].id, quotationActionType,
			message => {
				needRefreshDossierSubject.next();
				messageManagerService.pushInnerMessage(message);
			}, message => {
				messageManagerService.pushInnerMessage(message);
				loaderService.display(false);
			}
		);
	}

	static createWorkingAlert(
		alertService: AlertService,
		dossier: DossierModel,
		working: WorkingModel): void {
		const readers: AlertReaderWithCommodity[] = alertService.getAvailableReaderFromEntity(AuthorityEntityType.WORKING);
		readers.forEach(reader => {
			if (reader === NCReadersModel.SERVICE_POINT) {
				reader.commodity_id = working.commodity_id;
			}
		});
		alertService.writeNewAlert(
			MODULE_AUTHORITY_KEY,
			working.working_number_prefix,
			dossier.plate,
			dossier.chassis,
			[working.commodity_id],
			null,
			{
				contract_id: dossier.contractId,
				working_number: working.working_number_prefix,
				working_type: working.type,
				dossier_number: dossier.dossierNumberPrefix,
				dossier_status: dossier.status,
				[NotificationCenterUtilities.REDIRECT_URL_PREFIX]: dossier.dossierNumberPrefix,
				[NotificationCenterUtilities.REDIRECT_URL_UUID]: dossier.id
			},
			readers, AuthorityEntityType.WORKING
		);
	}

	static downloadQuotationPdf(
		dossier: DossierModel, working_id: string, quotation_id: string,
		working_number: string, plate: string, documentService: DocumentsService,
		loaderService: LoaderService, messageManagerService: MessageManagerService
	): void {

		loaderService.display(true);
		documentService.getActivityIdForDownloadQuotationPDF(dossier, working_id, quotation_id).subscribe(
			resp => {
				const loadDocument: BehaviorSubject<any> = new BehaviorSubject<any>({});
				let has_file: boolean = false, attemps: number = 0;

				loadDocument.pipe(
					takeWhile(() => !has_file),
					concatMap(
						_ => concat(
							documentService.downloadQuotationPdf(dossier, working_id, quotation_id, resp.id),
							of({}).pipe(
								delay(5000),
								// tslint:disable-next-line:no-shadowed-variable
								tap(_ => {
										loadDocument.next({});
										attemps++;

										if (attemps >= 3 && !has_file) {
											throw new Error();
										}
									}
								),
								skip(1),
							)
						)
					),
					map(response => response)
				).subscribe(
					file_data => {
						if (!!file_data) {
							has_file = true;
							Utilities.downloadBlob(
								file_data,
								`${DateUtilities.getStringDateFromDate(new Date()).split('-').join('')}_${working_number}_${plate}`
							);
							loaderService.display(false);
						}
					}, error => {
						this.handleErrorResponse(error, loaderService, messageManagerService);
						has_file = true;
					}
				);
			}, error => this.handleErrorResponse(error, loaderService, messageManagerService)
		);
	}

	protected static handleErrorResponse(error: any, loaderService: LoaderService, messageManagerService: MessageManagerService): void {
		messageManagerService.pushInnerMessage(MessageManagerUtilities.getMessageDetailsFromResponse(error));
		loaderService.display(false);
	}
}
